import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Language, languages } from '../data/languages';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useDropdownPosition } from '../hooks/useDropdownPosition';
import { useClickOutside } from '../hooks/useClickOutside';

interface SearchableDropdownProps {
    selectedValue: string | null;
    onSelect: (value: string | null) => void;
    placeholder?: string;
    disableNoLanguage?: boolean;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
    selectedValue,
    onSelect,
    placeholder = 'Select language...',
    disableNoLanguage = false,
}) => {
    // States
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const triggerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    // Use custom hooks for positioning and outside clicks
    const dropdownPosition = useDropdownPosition({
        isOpen,
        triggerRef,
        alignment: 'left',
        offsetY: 4,
        minWidth: 250,
    });

    useClickOutside({
        refs: [triggerRef, dropdownRef],
        isOpen,
        onClickOutside: () => setIsOpen(false),
    });

    // Find the selected language object
    const selectedLanguage = selectedValue
        ? languages.find(
              (lang) => lang.code.toLowerCase() === selectedValue.toLowerCase(),
          )
        : null;

    // Filter languages based on search term
    const filteredLanguages = languages.filter(
        (lang) =>
            lang.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            lang.code.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchRef.current) {
            searchRef.current.focus();
        }
    }, [isOpen]);

    // Reset search term when dropdown closes
    useEffect(() => {
        if (!isOpen) {
            setSearchTerm('');
        }
    }, [isOpen]);

    // Handle selecting an option
    const handleSelect = (lang: Language | null) => {
        onSelect(lang ? lang.code : null);
        setIsOpen(false);
    };

    // Toggle dropdown
    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    // Render dropdown menu using portal
    const renderDropdownMenu = () => {
        if (!isOpen) return null;

        const dropdownMenu = (
            <div
                className="dropdown-menu"
                ref={dropdownRef}
                role="listbox"
                tabIndex={-1}
                style={{
                    position: 'absolute',
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    width: `${dropdownPosition.width}px`,
                    minWidth: '250px',
                }}
            >
                <div className="search-container">
                    <input
                        ref={searchRef}
                        type="text"
                        className="search-input"
                        placeholder="Search languages..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
                <div className="dropdown-options">
                    {/* No language option at the top */}
                    {!disableNoLanguage && (
                        <>
                            <div
                                className={`dropdown-option ${
                                    selectedValue === null ? 'selected' : ''
                                }`}
                                onClick={() => handleSelect(null)}
                                role="option"
                                aria-selected={selectedValue === null}
                            >
                                <span className="language-name no-language">
                                    No language / Multiple languages
                                </span>
                            </div>

                            {/* Divider */}
                            <div className="dropdown-divider"></div>
                        </>
                    )}

                    {filteredLanguages.length > 0 ? (
                        filteredLanguages.map((lang) => (
                            <div
                                key={lang.code}
                                className={`dropdown-option ${
                                    selectedValue === lang.code
                                        ? 'selected'
                                        : ''
                                }`}
                                onClick={() => handleSelect(lang)}
                                role="option"
                                aria-selected={selectedValue === lang.code}
                            >
                                <span className="language-name">
                                    {lang.name}{' '}
                                    <span className="language-code">
                                        ({lang.code.toUpperCase()})
                                    </span>
                                </span>
                            </div>
                        ))
                    ) : (
                        <div className="no-results">No languages found</div>
                    )}
                </div>
            </div>
        );

        return ReactDOM.createPortal(dropdownMenu, document.body);
    };

    return (
        <div className="dropdown-container">
            <div
                ref={triggerRef}
                className="dropdown-trigger"
                onClick={toggleDropdown}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                {selectedLanguage ? (
                    <span
                        className={`language-tag editable selected ${isOpen ? 'open' : ''}`}
                    >
                        {selectedLanguage.name} (
                        {selectedLanguage.code.toUpperCase()})
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className="chevron-icon"
                        />
                    </span>
                ) : (
                    <span
                        className={`language-tag editable ${isOpen ? 'open' : ''}`}
                    >
                        {selectedValue === null ? 'No language' : placeholder}
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className="chevron-icon"
                        />
                    </span>
                )}
            </div>

            {renderDropdownMenu()}
        </div>
    );
};

export default SearchableDropdown;
